import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  type AppLocale,
  getStoredLocale,
  setAppLocale,
  SUPPORTED_LOCALES,
  isRtlLocale,
} from "@/i18n";

type LocaleContextValue = {
  locale: AppLocale;
  isRtl: boolean;
  setLocale: (locale: AppLocale) => Promise<void>;
  syncing: boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function normalizeLocale(value: unknown): AppLocale | null {
  if (typeof value !== "string") return null;
  return SUPPORTED_LOCALES.includes(value as AppLocale) ? (value as AppLocale) : null;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const branchId = user?.default_branch?.id;
  const [locale, setLocaleState] = useState<AppLocale>(getStoredLocale);
  const [syncing, setSyncing] = useState(false);

  const setLocale = useCallback(async (next: AppLocale) => {
    await setAppLocale(next);
    setLocaleState(next);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !branchId) return undefined;

    let cancelled = false;
    setSyncing(true);

    (async () => {
      try {
        const { data } = await api.get("/pos-settings", { params: { branch_id: branchId } });
        const fromBranch = normalizeLocale(data?.settings?.language);
        if (!cancelled && fromBranch && fromBranch !== locale) {
          await setAppLocale(fromBranch);
          setLocaleState(fromBranch);
        }
      } catch {
        /* keep stored locale */
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      locale,
      isRtl: isRtlLocale(locale),
      setLocale,
      syncing,
    }),
    [locale, setLocale, syncing],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}
